/**
 * Mocked event-property catalog keyed by trigger event_type.
 *
 * The backend does not yet publish a property registry, so these are authored
 * from the Postman collection, bonus v2 handler schemas, and the plan docs.
 * Replace this with a backend call to GET .../events/<type>/properties when
 * the API is available — the EventPropertyField component is the only consumer.
 */
export type EventProperty = {
  value: string;
  label: string;
  /** Hint for the value input (e.g. "number", "string"). */
  type: "number" | "string" | "boolean";
};

const BET_PLACEMENT_PROPERTIES: EventProperty[] = [
  { value: "bet_amount", label: "bet_amount", type: "number" },
  { value: "market_id", label: "market_id", type: "string" },
  { value: "sport_id", label: "sport_id", type: "string" },
  { value: "sr:tournament:id", label: "sr:tournament:id", type: "string" },
  { value: "sr:match:id", label: "sr:match:id", type: "string" },
  { value: "league_name", label: "league_name", type: "string" },
  { value: "odds", label: "odds", type: "number" },
];

export const EVENT_PROPERTY_CATALOG: Record<string, EventProperty[]> = {
  bet_placed: BET_PLACEMENT_PROPERTIES,
  places_bet_rebet_cash: BET_PLACEMENT_PROPERTIES,
  placed_live_bet: BET_PLACEMENT_PROPERTIES,
  places_profit_boost_bet: BET_PLACEMENT_PROPERTIES,
  deposit_completed: [
    { value: "amount",           label: "amount",           type: "number" },
    { value: "currency",         label: "currency",         type: "string" },
    { value: "deposit_id",       label: "deposit_id",       type: "string" },
    { value: "payment_method",   label: "payment_method",   type: "string" },
    { value: "is_first_deposit", label: "is_first_deposit", type: "boolean" },
    { value: "user_id",          label: "user_id",          type: "string" },
    { value: "state",            label: "state",            type: "string" },
  ],
  bet_settled: [
    { value: "stake",            label: "stake",            type: "number" },
    { value: "odds",             label: "odds",             type: "number" },
    { value: "outcome",          label: "outcome",          type: "string" },
    { value: "profit",           label: "profit",           type: "number" },
    { value: "bet_id",           label: "bet_id",           type: "string" },
    { value: "sport_id",         label: "sport_id",         type: "string" },
    { value: "user_id",          label: "user_id",          type: "string" },
    { value: "num_legs",         label: "num_legs (parlay)", type: "number" },
  ],
  settled_cash_bet: [
    { value: "bet_amount",       label: "bet_amount",       type: "number" },
    { value: "payout",           label: "payout",           type: "number" },
    { value: "bet_result",       label: "bet_result",       type: "string" },
    { value: "bet_id",           label: "bet_id",           type: "string" },
  ],
  parlay_loss_by_one: [
    { value: "bet_amount",       label: "bet_amount",       type: "number" },
    { value: "parlay_legs",      label: "parlay_legs (count)", type: "number" },
  ],
  "users:INSERT": [
    { value: "referral_code",    label: "referral_code (inviter)", type: "string" },
    { value: "referral_code_own",label: "referral_code_own",       type: "string" },
    { value: "country",          label: "country",                 type: "string" },
    { value: "state",            label: "state",                   type: "string" },
    { value: "platform",         label: "platform",                type: "string" },
    { value: "user_id",          label: "user_id",                 type: "string" },
  ],
  "kyc.verified": [
    { value: "user_id",          label: "user_id",    type: "string" },
    { value: "level",            label: "level",      type: "string" },
    { value: "country",          label: "country",    type: "string" },
  ],
};

/** Fallback list shown when the trigger_key has no entry in the catalog. */
export const FALLBACK_PROPERTIES: EventProperty[] = [
  { value: "user_id",  label: "user_id",  type: "string" },
  { value: "amount",   label: "amount",   type: "number" },
];

export function getPropertiesForTrigger(eventType: string | undefined): EventProperty[] {
  if (!eventType) return FALLBACK_PROPERTIES;
  return EVENT_PROPERTY_CATALOG[eventType] ?? FALLBACK_PROPERTIES;
}
